import React, {useEffect, useState} from 'react'
import {useSelector} from 'react-redux';
import DataTable from 'react-data-table-component';
import {Link, useLocation} from "react-router-dom";
import {get_budget_surrender} from "../../helper/Api";
import { AddCommasToAmount } from "../../helper/Utils";
import {AiOutlineEye} from 'react-icons/ai';
import { Card, CardContent, Tooltip } from '@mui/material';
import moment from 'moment';
import './BudgetSurrender.css';

const getApprovalBadgeClass = (approval) => {
    if (approval === null || approval === undefined || approval === '') {
        return 'bg-warning-20';
    }
    if (String(approval) === '1') {
        return 'bg-success-20';
    }
    if (String(approval) === '0') {
        return 'bg-danger-20';
    }
    return 'bg-warning-20';
};

const getApprovalIconClass = (approval) => {
    if (approval === null || approval === undefined || approval === '') {
        return 'icon-warning_amber text-warning-100';
    }
    if (String(approval) === '1') {
        return 'icon-verified text-success-100';
    }
    if (String(approval) === '0') {
        return 'icon-warning_amber text-danger-100';
    }
    return 'icon-warning_amber text-warning-100';
};

const getSchemeDisplayName = (row) => {
    const code = row.scheme_code || '';
    const name = row.name || '';
    if (!code) return name;
    if (name.startsWith(`${code}-`)) return name.slice(code.length + 1);
    if (name.startsWith(code)) return name.slice(code.length).replace(/^-/, '');
    return name;
};

const ApprovalStatusCell = ({ row }) => (
    <div className="budget-surrender-approval-cell">
        <span className={`budget-surrender-approval-badge ${getApprovalBadgeClass(row.l2_approval)}`}>
            L2 <i className={`${getApprovalIconClass(row.l2_approval)} fs-16px`}></i>
        </span>
        <span className={`budget-surrender-approval-badge ${getApprovalBadgeClass(row.l3_approval)}`}>
            L3 <i className={`${getApprovalIconClass(row.l3_approval)} fs-16px`}></i>
        </span>
    </div>
);

export default function BudgetSurrender(filterData) {
    const {authData} = useSelector((state) => state.authData);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const {state} = useLocation();
    const [data, setData] = useState(state && state.data ? state.data : []);

    const formatAmount = (value) => {
        if (value === null || value === undefined || value === '') return '-';
        return AddCommasToAmount(value);
    };

    const customStyles = {
        headCells: {
            style: {
                fontWeight: "bold",
                fontSize: "14px"
            },
        },
    };

    const columns = [
        {
            name: 'Actions',
            cell: (row) => (
                <div className="budget-surrender-actions">
                    <Tooltip title="View Surrender" arrow placement="top">
                        <span className="budget-surrender-action-tooltip-wrap">
                            <Link
                                to={`/view-budget-surrender/${row.batch_id}`}
                                className="budget-surrender-action-btn budget-surrender-action-btn--view"
                            >
                                <AiOutlineEye size={16} />
                            </Link>
                        </span>
                    </Tooltip>
                </div>
            ),
            wrap: true,
            center: true,
            width: '110px',
        },
        {
            name: 'Order No',
            selector: (row) => row.order_no,
            wrap: true,
            width: "100px"
        },
        {
            name: 'Order Date',
            selector: (row) => row.order_date
                ? moment(row.order_date, 'YYYY-MM-DD').format('DD-MM-YYYY')
                : '',
            wrap: true,
            width: "150px"
        },
        {
            name: 'Dept Name',
            selector: (row) => row.department_name,
            wrap: true,
            width: "150px"
        },
        {
            name: 'Dept Level',
            selector: (row) => row.department_level,
            wrap: true,
            width: "150px"
        },
        {
            name: 'Financial Year',
            selector: (row) => row.start_year+'-'+row.end_year,
            wrap: true,
            width: "150px"
        },
        {
            name: 'Scheme',
            cell: (row) => {
                const schemeName = getSchemeDisplayName(row);
                return (
                    <span className="budget-surrender-scheme-cell">
                        <strong>{row.scheme_code}</strong>
                        {schemeName ? ` - ${schemeName}` : ''}
                    </span>
                );
            },
            wrap: true,
            minWidth: '220px',
        },
        {
            name: 'Amount',
            selector: (row) => formatAmount(row.order_value),
            wrap: true,
            width: "100px"
        },
        {
            name: 'Approval Status',
            cell: (row) => <ApprovalStatusCell row={row} />,
            wrap: true,
            center: true,
            minWidth: '160px',
        },
    ];

    const handleChangeRowsPerPage = (currentRowsPerPage) => {
        setRowsPerPage(currentRowsPerPage);
    };

    const fetchChallans = async () => {
        try {
            const params = {
                department_id: filterData.department_id || authData?.user?.department_details?.id,
                financial_year_id: filterData.fyId,
            };
            if (filterData.pending === true) {
                params.pending = true;
            }
            if (filterData.approved === true) {
                params.approval = true;
            }
            const resp = await get_budget_surrender(params);
            if (resp.data.status) {
                setData(resp.data.result);
            }
        } catch (error) {
            setData([]);
        }
    };

    useEffect(() => {
        fetchChallans();
    }, [
        filterData.fromDate,
        filterData.toDate,
        filterData.dateColumn,
        filterData.searchStr,
        filterData.department_id,
        filterData.pending,
        filterData.approved,
        filterData.fyId,
    ]);

    return (
        <div>
            <Card>
                <CardContent>
                    <DataTable
                        customStyles={customStyles}
                        className="dataTables_wrapper"
                        columns={columns}
                        data={data}
                        fixedHeader
                        fixedHeaderScrollHeight="600px"
                        pagination
                        paginationPerPage={rowsPerPage}
                        paginationTotalRows={data.length}
                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                        onChangeRowsPerPage={handleChangeRowsPerPage}
                    />
                </CardContent>
            </Card>
        </div>
    );
}
